
import Moment from "moment";
import { Component, Mixins, Model, Prop, Watch, Emit } from "vue-property-decorator";
import { DomainRegistry } from "../../../../domain";
import { DocumentSummary, DocumentTypeKinds } from "../../../../domain/document";
import { IConfiguration } from "../../../../domain/configuration";
import InfiniteLoading from "vue-infinite-loading";
import { Table, TableColumn } from "element-ui";
import { SortOrder } from "element-ui/types/table";
import { ISortParameter } from "../../../system";
import { ForceUseInfiniteWrapperForInfiniteLoadingMixin } from "../../../system/mixins";

@Component
export default class DocumentSelectorMixin extends Mixins(ForceUseInfiniteWrapperForInfiniteLoadingMixin) {
  @Model("input")
  value: DocumentSummary;

  @Prop()
  documents: DocumentSummary[];

  @Prop()
  sortParameter: ISortParameter;

  @Prop()
  shouldShowPatientColumns: boolean;

  $refs!: {
    table: Table;
    infiniteLoading: InfiniteLoading;
  };

  get hasDocuments(): boolean {
    return (0 < this.documentCount);
  }

  get documentCount(): number {
    return this.documents.length;
  }

  get loadChunkSize(): number {
    const config = this.$store.getters.configuration as IConfiguration;
    const settingCount = config.behavior.document.list.infiniteLoader.rowCount;
    return settingCount ? settingCount : 30; // 設定がなかった時のためにデフォルト値
  }

  get unpublishedColor(): string {
    return this.$configuration.style.document.list.selector.style.unpublished.color;
  }

  get deletedRowBackground(): string {
    return this.$configuration.style.document.list.selector.style.row.deleted.backgroundColor;
  }

  get temporaryRowBackground(): string {
    return this.$configuration.style.document.list.selector.style.row.temporary.backgroundColor;
  }

  rowStyle(rowData: {row: DocumentSummary, rowIndex: number}): any {
    return {
      background: rowData.row.isDeleted
        ? this.deletedRowBackground
        : rowData.row.isTemporary
          ? this.temporaryRowBackground
          : "#fdf9f2",
    };
  }

  @Watch("documents")
  onDocumentsUpdated(newValue: DocumentSummary[], oldValue: DocumentSummary[]): void {
    this.$logger.debug("onDocumentsUpdated..");
    this.input(null);

    this.loadedDocuments = [];
    this.chunkedCount = 0;

    this.resetInfiniteLoading();
  }

  @Watch("sortParameter")
  onSortParameterUpdated(newValue: ISortParameter, oldValue: ISortParameter): void {
    if (!oldValue || (newValue.property !== oldValue.property || newValue.order !== oldValue.order)) {
      this.updateSort();
    }
  }

  @Emit()
  input(value: DocumentSummary): void {}

  data(): any {
    return {
      chunkedCount: 0,
      loadedDocuments: [],
    };
  }
  chunkedCount: number;
  loadedDocuments: DocumentSummary[];

  mounted(): void {
    this.updateForceUseInfiniteWrapper(this.$refs.table.$el as HTMLElement, this.$refs.infiniteLoading);
    this.updateSort();
  }

  toDateString(source: Date): string {
    return Moment(source).format("YY/MM/DD");
  }

  toTimeString(source: Date): string {
    return Moment(source).format("HH:mm");
  }

  isWorkflowDocument(documentType: DocumentTypeKinds): boolean {
    return documentType === DocumentTypeKinds.Workflow;
  }

  resetInfiniteLoading(): void {
    if (this.$refs.infiniteLoading) {
      this.$logger.debug("call reset.");
      this.$refs.infiniteLoading.stateChanger.reset();
    }
  }

  onInfinite(): void {
    this.$logger.debug("onInfinite");
    this.chunkList();
    // element-uiのテーブルにスクロールバーが表示される際、ヘッダがずれる問題に対応するため、dataプロパティ更新時に遅延でdoLayoutを呼び出す
    this.$nextTick(() => this.$doWhenValid(t => t.doLayout(), this.$refs.table));
  }

  private chunkList(): void {
    this.$logger.debug("chunkList");
    for (let i = this.chunkedCount; i < (this.chunkedCount + this.loadChunkSize); i++) {
      if (i >= this.documents.length) {
        this.chunkedCount = this.documents.length;

        this.$logger.debug("call complete. i is " + i);
        this.$refs.infiniteLoading.stateChanger.complete();
        return;
      }

      this.loadedDocuments.push(this.documents[i]);
    }
    this.chunkedCount += this.loadChunkSize;

    this.$logger.debug("call loaded. chunkedCount is " + this.chunkedCount);
    this.$refs.infiniteLoading.stateChanger.loaded();
  }

  clearSelection() {
    this.$refs.table.setCurrentRow();
  }

  updateSort(): void {
    if (this.$refs.table && this.sortParameter) {
      this.$refs.table.sort(this.sortParameter.property, this.sortParameter.order);
    }
  }

  onSortChanged(param: { column: TableColumn, prop: string, order: SortOrder }): void {
    // element-uiのテーブルソートは ascending → descending → null となるため null の場合は ascending とする
    const parameter: ISortParameter = {
      property: param.prop,
      order: param.order ? param.order : "ascending"
    };
    this.$emit("sortChange", parameter);
  }

  async handleSelectionChanged(row: DocumentSummary): Promise<void> {
    if (!row) {
      return;
    }
    this.input(null); // 一旦クリアするためnullを通知する
    try {
      await this.checkReadable(row);
      const result = await this.isConfirmedReadable(row);
      if (!result) {
        this.clearSelection();
        return;
      }
      await this.checkLogin(row);
      await this.checkWriteAuditLog(row);
      this.input(row);
    } catch (e) {
      this.clearSelection();
    }
  }

  private async checkReadable(row: DocumentSummary): Promise<void> {
    if (row.isReadable) { return; }
    await this.$commonDialogs.confirmation.show({
      title: null, 
      message: ["この書類の閲覧は許可されていません。"],
      buttons: [],
      cancellation: {
        isEnable: true,
        label: "中止する",
      },
    });
    throw new Error("この書類の閲覧は許可されていません。");
  }

  private async isConfirmedReadable(row: DocumentSummary): Promise<boolean> {
    if (!row.shouldConfirmReadable) { return true; }
    const index = await this.$commonDialogs.confirmation.confirmDangerCancel(
      null,
      row.shouldWriteAuditLog
        ? ["この書類の閲覧は許可されていません。閲覧履歴は管理者に通知されます。", "処理を続行しますか？"]
        : ["この書類の閲覧は許可されていません。処理を続行しますか？"],
      "続行する"
    );
    return (index === 0);
  }

  private async checkLogin(row: DocumentSummary): Promise<void> {
    if (row.shouldLogin) {
      await this.$commonDialogs.authentication.authenticator.show();
    }
  }

  private async checkWriteAuditLog(row: DocumentSummary): Promise<void> {
    if (row.shouldWriteAuditLog) {
      const service = DomainRegistry.document.getDocumentRepository();
      try {
        await service.registerAuditLog(row);
      } catch (e) {
        this.$error(
          "閲覧履歴通知失敗",
          "閲覧履歴通知でエラーが発生しました。",
          e,
        );
        throw e;
      }
    }
  }

  @Emit("documentDoubleClick")
  onRowDoubleClicked(): void {}

  shouldShowNormalDescription(row: DocumentSummary): boolean {
    return !!this.toNormalDescription(row);
  }
  
  toNormalDescription(row: DocumentSummary): string {
    // TODO: ワークフロー文書については医師記載進捗を過ぎたかどうかで表示文言を切り替える機能があったほうが良さそう
    if (!row || row.isDeleted || row.isFixed || row.isTemporary || row.documentType !== DocumentTypeKinds.Normal) { return ""; }
    return this.$configuration.wording.document.list.status.normal;
  }

  clearSort(): void {
    this.$refs.table.clearSort();
  }
}
